import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, FileText, Tag, Calendar, Clock, Eye } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Tooltip } from '../../components/Tooltip';
import type { BlogPost } from '../../schemas/validation';
import type { PublishStatus } from '../../hooks/useUnpublishedSummary';

/** Display-friendly status */
type DisplayPublishStatus = 'draft' | 'unpublished_changes' | 'published';

/** Map backend canonical status + post row to list badge / labels */
function toListDisplayStatus(post: BlogPost, backend?: PublishStatus): DisplayPublishStatus {
  const recordSaysPublished = post.status === 'published' || !!post.publishedAt;

  if (backend === 'changed') return 'unpublished_changes';
  if (backend === 'up_to_date') return 'published';
  if (backend === 'never_published') {
    return recordSaysPublished ? 'published' : 'draft';
  }
  if (recordSaysPublished) return 'published';
  return 'draft';
}

interface BlogPostsListProps {
  siteId: string;
  posts: BlogPost[];
  isLoading: boolean;
  isTogglingPublish: string | null;
  currentPage: number;
  totalPages: number;
  totalPosts: number;
  postsPerPage: number;
  onTogglePublish: (post: BlogPost) => void;
  onDelete: (post: BlogPost) => void;
  onPageChange: (page: number) => void;
  /** Backend-computed status per blog post (from unpublished-summary) */
  blogPostStatusMap?: Record<string, PublishStatus>;
}

export const BlogPostsList: React.FC<BlogPostsListProps> = ({
  siteId,
  posts,
  isLoading,
  isTogglingPublish,
  currentPage,
  totalPages,
  totalPosts,
  postsPerPage,
  onTogglePublish,
  onDelete,
  onPageChange,
  blogPostStatusMap,
}) => {
  const navigate = useNavigate();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 spinner"></div>
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="card">
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No posts yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating your first blog post.
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate(`/sites/${siteId}/blog/posts/new`)}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <Plus size={20} />
              <span>Create First Post</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="divide-y divide-gray-200">
        {posts.map(post => (
          <div key={post.id} className="p-4 hover:bg-gray-50 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">{post.title}</h3>
                  {/* Publish Status Badge (from backend summary when available) */}
                  {(() => {
                    const status = toListDisplayStatus(post, blogPostStatusMap?.[post.id]);
                    if (status === 'draft')
                      return (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Draft
                        </span>
                      );
                    if (status === 'unpublished_changes')
                      return (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          Unpublished changes
                        </span>
                      );
                    return (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Published
                      </span>
                    );
                  })()}
                </div>

                {post.excerpt && (
                  <p className="mt-1 text-sm text-gray-600 line-clamp-2">{post.excerpt}</p>
                )}

                <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                  <span className="flex items-center space-x-1">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(post.createdAt)}</span>
                  </span>
                  {post.readingTimeMinutes && (
                    <span className="flex items-center space-x-1">
                      <Clock className="h-4 w-4" />
                      <span>{post.readingTimeMinutes} min read</span>
                    </span>
                  )}
                  {post.authorName && <span>By {post.authorName}</span>}
                  {post.viewCount > 0 && (
                    <span className="flex items-center space-x-1">
                      <Eye className="h-4 w-4" />
                      <span>{post.viewCount} views</span>
                    </span>
                  )}
                </div>

                {post.tags && post.tags.length > 0 && (
                  <div className="mt-2 flex items-center space-x-2">
                    <Tag className="h-4 w-4 text-gray-400" />
                    <div className="flex flex-wrap gap-1">
                      {post.tags.map(tag => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-gray-100 text-gray-600"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => onTogglePublish(post)}
                  disabled={isTogglingPublish === post.id}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors',
                    post.publishedAt
                      ? 'text-orange-600 bg-orange-50 hover:bg-orange-100'
                      : 'text-green-600 bg-green-50 hover:bg-green-100'
                  )}
                >
                  {isTogglingPublish === post.id ? (
                    <div className="w-4 h-4 animate-spin border-2 border-current border-t-transparent rounded-full" />
                  ) : post.publishedAt ? (
                    'Unpublish'
                  ) : (
                    'Publish'
                  )}
                </button>

                <Tooltip content="Edit Post">
                  <button
                    onClick={() => navigate(`/sites/${siteId}/blog/posts/${post.id}`)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Edit size={18} />
                  </button>
                </Tooltip>

                <Tooltip content="Delete Post">
                  <button
                    onClick={() => onDelete(post)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            Showing {(currentPage - 1) * postsPerPage + 1} to{' '}
            {Math.min(currentPage * postsPerPage, totalPosts)} of {totalPosts} posts
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
